import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CaptionsSpokenLanguage } from 'features/application/types';
import {
  CaptionsChunk,
  CaptionsReceivedPayload,
  CaptionsState,
} from 'features/transcripts/slices/captions/types';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { UserId } from 'features/users/types';
import { RootState } from 'store/store';
import { signalingRoomJoined } from 'features/room/actions';
import { signalingTranscriptionSpokenLanguageChanged } from 'features/transcripts/actions';
import { selectFeatureFlag } from 'features/feature-set/featuresSlice';
import { selectIsBreakoutRoom } from 'features/breakout-rooms/breakoutRoomsSlice';

export const initialState: CaptionsState = {
  textReceived: false,
  displayCaptions: false,
  mode: 'end_of_speech',
  chunkOrder: [],
  chunks: {},
  fontSize: 24,
  resetTimers: {},
  spokenLanguage: 'en',
  sessionSpokenLanguage: 'en',
  applySpokenLanguageToAll: true,
};

const emptyChunk = {
  chunkFinalized: false,
  speechFinalized: false,
  text: '',
  buffer: '',
  tempText: '',
};

export const captionsSlice = createSlice({
  name: 'captions',
  initialState,
  reducers: {
    captionsToggled(state, action: PayloadAction<boolean | undefined>) {
      if (action.payload === undefined) {
        state.displayCaptions = !state.displayCaptions;
      } else {
        state.displayCaptions = action.payload;
      }

      if (state.displayCaptions) {
        // reset to initial state;
        // see if this doesnt conflict with first captionsReceived??;
        // @TODO: see if we need this 'reset' logic
        // state.text = initialState.text;
      }
    },
    captionsReceived(state, action: PayloadAction<CaptionsReceivedPayload>) {
      const { chunk, timer } = action.payload;

      const userId = chunk.participant.id;
      const lastDisplayedSpeakerId = state.chunkOrder[state.chunkOrder.length - 1];
      const speakerAlreadyDisplayed = state.chunkOrder.includes(userId);
      const speakerInterrupts = lastDisplayedSpeakerId !== userId;

      state.textReceived = true;
      state.resetTimers[userId] = timer;

      // update prompt;
      if (!state.chunks[userId]) {
        state.chunks[userId] = {
          ...emptyChunk,
          participant: chunk.participant,
          phone: chunk.phone,
        };
      }

      let updateOrder = true;

      if (speakerInterrupts) {
        // new prompt from interrupting user, reset text
        state.chunks[userId].text = '';
        state.chunks[userId].tempText = '';
        state.chunks[userId].buffer = '';
      }

      // temp (?) hack to work better with speechmatics output;
      chunk.transcript = chunk.transcript.replace(/^(,|\.|\?|!|\s)/, '');

      // replicate live speech behavior with continuing messages;
      if (state.mode === 'end_of_speech') {
        state.chunks[userId].text += ` ${chunk.transcript}`;

        if (state.chunks[userId].text.length > 2000) {
          state.chunks[userId].text = state.chunks[userId].text.slice(-2000);
        }
      } else if (chunk.chunkFinalized) {
        if (speakerInterrupts) {
          updateOrder = !speakerAlreadyDisplayed;
        }

        state.chunks[userId].buffer += ` ${chunk.transcript}`;

        if (state.chunks[userId].buffer.length > 2000) {
          state.chunks[userId].buffer = state.chunks[userId].buffer.slice(-2000);
        }

        state.chunks[userId].text = state.chunks[userId].buffer;

        state.chunks[userId].tempText = '';
      } else {
        state.chunks[userId].tempText = chunk.transcript;
      }

      //  update order
      if (updateOrder && (speakerInterrupts || !speakerAlreadyDisplayed)) {
        state.chunkOrder = lastDisplayedSpeakerId ? [lastDisplayedSpeakerId, userId] : [userId];
      }
    },
    fontSizeChanged(state, action: PayloadAction<number>) {
      state.fontSize = action.payload;
    },
    captionsReset(state, action: PayloadAction<UserId>) {
      state.chunkOrder = state.chunkOrder.filter((id) => id !== action.payload);

      delete state.chunks[action.payload];
    },
    spokenLanguageChanged(
      state,
      action: PayloadAction<{ code: CaptionsSpokenLanguage; applyToAll?: boolean }>
    ) {
      const { code, applyToAll } = action.payload;

      state.spokenLanguage = code;
      if (applyToAll !== undefined) {
        state.applySpokenLanguageToAll = applyToAll;
      }
    },
    spokenLanguageApplyToAllChanged(state, action: PayloadAction<boolean>) {
      state.applySpokenLanguageToAll = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.fulfilled, (state, action) => {
        state.mode = action.payload.settings.captionsMode;
      })
      .addCase(signalingRoomJoined, (state, action) => {
        state.spokenLanguage = action.payload.captionsLanguage;
        state.sessionSpokenLanguage = action.payload.captionsLanguage;
      })
      .addCase(signalingTranscriptionSpokenLanguageChanged, (state, action) => {
        const { data } = action.payload;
        if (data.applyToAll) {
          state.spokenLanguage = data.code;
          state.sessionSpokenLanguage = data.code;
        }
      })
      .addCase(SDKConnected, (state, action) => {
        const options = action.payload;

        if (options.showCaptions !== undefined) {
          state.displayCaptions = options.showCaptions;
        }
      });
  },
});

export const {
  captionsReceived,
  captionsToggled,
  fontSizeChanged,
  captionsReset,
  spokenLanguageChanged,
  spokenLanguageApplyToAllChanged,
} = captionsSlice.actions;

export default captionsSlice.reducer;

export const selectCaptionsDisplayed = (state: RootState) =>
  state.transcripts.captions.displayCaptions;
export const selectCaptionTextReceived = (state: RootState) =>
  state.transcripts.captions.textReceived;

export const selectCaptionsResetTimer = (state: RootState, userId: UserId) =>
  state.transcripts.captions.resetTimers[userId];

export const selectCaptionsText = createSelector(
  (state: RootState) => state.transcripts.captions,
  (state) => [state.chunkOrder, state.chunks] as [string[], Record<UserId, CaptionsChunk>]
);

export const selectCaptionsFontSize = (state: RootState) => state.transcripts.captions.fontSize;
export const selectCaptionsSpokenLanguage = (state: RootState) =>
  state.transcripts.captions.spokenLanguage;

// spokenLanguage is whatever transcribes this participant, which a personal pick moves away from
// the session's own language. Only a room join or a change applied to everyone brings them back.
export const selectSpokenLanguageOverridden = createSelector(
  [
    selectCaptionsSpokenLanguage,
    (state: RootState) => state.transcripts.captions.sessionSpokenLanguage,
  ],
  (spokenLanguage, sessionSpokenLanguage) => spokenLanguage !== sessionSpokenLanguage
);

export const selectCaptionsApplyToAll = (state: RootState) =>
  state.transcripts.captions.applySpokenLanguageToAll;

// The Closed captions settings tab is the only place the spoken language can be changed,
// and RoomSettings hides it in breakout rooms and when the captions feature is off.
export const selectCaptionsSettingsAvailable = createSelector(
  [(state: RootState) => selectFeatureFlag(state, 'captions'), selectIsBreakoutRoom],
  (captionsEnabled, isBreakoutRoom) => captionsEnabled && !isBreakoutRoom
);
